import { useCallback, useRef } from 'react';

import uniq from 'lodash/uniq';
import { di } from 'react-magnetic-di';
import { useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY } from '../../../common/constants';
import {
	LOOKUP_SOURCE_COMPONENTS_ASYNC_DATA,
	LOOKUP_SOURCE_PROJECTS_ASYNC_DATA,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
	LOOKUP_SOURCE_VERSIONS_ASYNC_DATA,
} from '../../../common/constants';
import type { ExecutionContext } from '../../../common/types/execution-context';
import type { FieldMapFromIframe } from '../../../common/types/field';
import type {
	LookupComponents,
	LookupData,
	LookupProjects,
	LookupUsers,
	LookupVersions,
} from '../../../common/types/lookup';
import { isNotAutomaticId } from '../../../common/utils/is-not-automatic-id';
import { convertProjectIdsToAris } from '../../../common/utils/project-ari';
import { fetchComponents } from '../../../services/fetch-components';
import { fetchProjects } from '../../../services/fetch-projects';
import { fetchUsers } from '../../../services/fetch-users';
import { fetchVersions } from '../../../services/fetch-versions';
import { useExecutionContext } from '../../execution-context';
import { useFormMetadata } from '../../issue-adjustments/hooks/use-form-metadata';
import { useViewType } from '../../issue-adjustments/hooks/use-view-type';
import { useTenantContext } from '../../tenant-context';
import { getSupportedFieldConfiguration } from '../../view-configuration';
import type { AsyncFieldsValues } from './types';

const getProjectScope = (
	executionContext: ExecutionContext,
): { id: string; key: string } | null => {
	const { project } = executionContext;
	if (!project || typeof project !== 'object' || !('id' in project) || !('key' in project)) {
		return null;
	}

	const { id, key } = project;
	return typeof id === 'string' && typeof key === 'string' ? { id, key } : null;
};

export const useLookupFieldData = (): ((
	fieldsChanges: FieldMapFromIframe,
) => Promise<Omit<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>>) => {
	di(
		useRelayEnvironment,
		useFormMetadata,
		fetchUsers,
		fetchProjects,
		fetchComponents,
		fetchVersions,
		useViewType,
		useTenantContext,
		useExecutionContext,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useTenantContext();
	const environment = useRelayEnvironment();

	const formMetadata = useFormMetadata();
	const formMetadataRef = useRef(formMetadata);
	const viewType = useViewType();
	const executionContext = useExecutionContext();
	const executionContextRef = useRef(executionContext);

	/**
	 * We want to have an up-to-date data when this hook's
	 * returned function is being called in useSubmit callback function
	 * thats why we keep it in a ref object
	 */
	formMetadataRef.current = formMetadata;
	executionContextRef.current = executionContext;

	return useCallback(
		async (
			fieldsChanges: FieldMapFromIframe,
		): Promise<Omit<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>> => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const lookupData = {} as Omit<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>;

			const asyncFieldsValues = Object.entries(fieldsChanges).reduce(
				(acc: AsyncFieldsValues, [fieldId, fieldData]) => {
					if (!formMetadataRef.current[fieldId]) {
						return acc;
					}

					const { fieldType } = formMetadataRef.current[fieldId];
					const fieldValue = fieldData.value;
					const supportedFieldsConfiguration = getSupportedFieldConfiguration(viewType, fieldType);

					const lookupSource = supportedFieldsConfiguration?.lookupSource;
					const isUserLookupField = lookupSource === LOOKUP_SOURCE_USERS_ASYNC_DATA;
					const isProjectLookupField = lookupSource === LOOKUP_SOURCE_PROJECTS_ASYNC_DATA;
					const isComponentLookupField = lookupSource === LOOKUP_SOURCE_COMPONENTS_ASYNC_DATA;
					const isVersionLookupField = lookupSource === LOOKUP_SOURCE_VERSIONS_ASYNC_DATA;

					if (isUserLookupField && fieldValue && isNotAutomaticId(fieldValue)) {
						const currentAccountIds = acc.accountIds ? [...acc.accountIds] : [];
						const newAccountIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.accountIds = [...currentAccountIds, ...newAccountIds];
					}

					if (isProjectLookupField && fieldValue) {
						const currentProjectIds = acc.projectIds ? [...acc.projectIds] : [];
						const newProjectIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.projectIds = [...currentProjectIds, ...newProjectIds];
					}

					if (isComponentLookupField && fieldValue) {
						const currentComponentIds = acc.componentIds ? [...acc.componentIds] : [];
						const newComponentIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.componentIds = [...currentComponentIds, ...newComponentIds];
					}

					if (isVersionLookupField && fieldValue) {
						const currentVersionIds = acc.versionIds ? [...acc.versionIds] : [];
						const newVersionIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.versionIds = [...currentVersionIds, ...newVersionIds];
					}

					return acc;
				},
				{},
			);
			let users: LookupUsers = [];
			let projects: LookupProjects = [];
			let components: LookupComponents = [];
			let versions: LookupVersions = [];

			if (asyncFieldsValues.accountIds) {
				const uniqueAccountIds = uniq(asyncFieldsValues.accountIds);
				users = await fetchUsers(environment, uniqueAccountIds, createAnalyticsEvent);
			}

			if (asyncFieldsValues.projectIds) {
				const uniqueProjectIds = uniq(asyncFieldsValues.projectIds);

				// Convert numeric project IDs to ARIs before fetching
				// GraphQL expects ARIs like "ari:cloud:jira:{cloudId}:project/10001", not plain "10001"
				const projectAris = convertProjectIdsToAris(uniqueProjectIds, cloudId);
				projects = await fetchProjects(environment, projectAris, createAnalyticsEvent);
			}

			const projectScope = getProjectScope(executionContextRef.current);

			if (asyncFieldsValues.componentIds?.length && projectScope) {
				const uniqueComponentIds = uniq(asyncFieldsValues.componentIds);
				components = await fetchComponents(
					environment,
					cloudId,
					projectScope.key,
					uniqueComponentIds,
					createAnalyticsEvent,
				);
			}

			if (asyncFieldsValues.versionIds?.length && projectScope) {
				const uniqueVersionIds = uniq(asyncFieldsValues.versionIds);
				versions = await fetchVersions(
					environment,
					cloudId,
					projectScope.key,
					uniqueVersionIds,
					createAnalyticsEvent,
				);
			}

			lookupData[LOOKUP_SOURCE_USERS_ASYNC_DATA] = users;
			lookupData[LOOKUP_SOURCE_PROJECTS_ASYNC_DATA] = projects;
			lookupData[LOOKUP_SOURCE_COMPONENTS_ASYNC_DATA] = components;
			lookupData[LOOKUP_SOURCE_VERSIONS_ASYNC_DATA] = versions;

			return lookupData;
		},
		[createAnalyticsEvent, viewType, environment, cloudId],
	);
};
